import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { useAuth } from '../../contexts/AuthContext';
import competitorService, { Competitor, CompetitorSearchFilters } from '../../services/competitorService';
import financeService, { FinanceTransaction, MPESAPaymentRequest } from '../../services/financeService';
import profileReminderService, { ProfileCompletenessData } from '../../services/profileReminderService';
import { api } from '../../utils/api';
import CompetitionInterface from './CompetitionInterface';
import GroupCompetitionModal from './GroupCompetitionModal';
import TicketManagement from './TicketManagement';
import TicketPurchase from './TicketPurchase';
import CompetitorChatModal from './CompetitorChatModal';
import CompetitorConversations from './CompetitorConversations';
import CompetitorProfile from './CompetitorProfile';
import ViewProfileModal from './ViewProfileModal';
import ChallengeRequestModal from './ChallengeRequestModal';
import NotificationCenter from '../shared/NotificationCenter';
import CAPEIntegration from './CAPEIntegration';
import CAPEEnhancedCompetitionInterface from './CAPEEnhancedCompetitionInterface';
import capeService from '../../services/capeService';
import useCAPEProfileSync from '../../hooks/useCAPEProfileSync';
import { useNavigate } from 'react-router-dom';

// Types
interface User {
  id: string;
  name: string;
  accountType: 'student' | 'public';
  gradeLevel?: number;
  university?: string;
  rating: number;
  wins: number;
  totalMatches: number;
  avatar?: string;
  profilePicture?: string;
  isOnline: boolean;
}

interface CompetitorExtended extends Competitor {
  lastSeen?: string;
  currentStake?: number;
  isOnline: boolean;
  // Add educational and location information
  educationalInfo?: {
    isCurrentlyStudying: boolean;
    level: 'primary_school' | 'form_4_leaver' | 'tvet' | 'college' | 'university' | 'postgraduate' | 'not_studying';
    institution?: string;
    courseOfStudy?: string;
    yearOfStudy?: number;
    expectedGraduationYear?: number;
  };
  personalInfo?: {
    age: number;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    region: string;
    province: string;
    city?: string;
    country: string;
    timezone: string;
  };
}

interface LeaderboardEntry {
  rank: number;
  user: User;
  score: number;
  category: string;
}

// Filter types
interface CompetitorFilters {
  status: 'online' | 'offline' | 'all';
  accountType: 'student' | 'public' | 'all';
  minRating: number;
  maxRating: number;
}

// MPESAPayment interface for local use
interface MPESAPayment {
  phoneNumber: string;
  amount: number;
  reference: string;
  description: string;
}

// Styled Components
const DashboardContainer = styled.div`
  background: #ffffff;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  padding-bottom: 100px; /* Increased to accommodate bottom nav */
  
  /* Ensure proper mobile scaling */
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  
  /* Mobile-first responsive design */
  @media (max-width: 480px) {
    padding: 0 0 100px 0;
    font-size: 14px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    padding: 0 0 100px 0;
    font-size: 16px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  @media (min-width: 769px) {
    padding: 0 20px 100px 20px;
    font-size: 18px;
  }
  
  /* Ensure all child elements respect container boundaries */
  * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  background: linear-gradient(45deg, #ffd700, #ffed4e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 20px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
`;

const StatIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 10px;
  color: #ffd700;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
`;



const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;



const CompetitorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CompetitorCard = styled.div<{ isOnline: boolean }>`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  border: 1px solid ${props => props.isOnline ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
  }
`;

const Avatar = styled.div<{ isOnline: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.isOnline ? 'linear-gradient(45deg, #4caf50, #8bc34a)' : 'linear-gradient(45deg, #9e9e9e, #757575)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.2rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.isOnline ? '#4caf50' : '#9e9e9e'};
    border: 2px solid white;
  }
`;

const CompetitorInfo = styled.div`
  flex: 1;
`;

const CompetitorName = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
`;

const CompetitorDetails = styled.div`
  font-size: 0.8rem;
  opacity: 0.7;
`;

const ChallengeButton = styled.button`
  background: linear-gradient(45deg, #ff6b6b, #ee5a24);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(255, 107, 107, 0.4);
  }
`;

const MessageButton = styled.button`
  background: linear-gradient(45deg, #1877f2, #166fe5);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(24, 119, 242, 0.4);
    background: linear-gradient(45deg, #166fe5, #1464d0);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LeaderboardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Rank = styled.div<{ rank: number }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  background: ${props => {
    if (props.rank === 1) return 'linear-gradient(45deg, #ffd700, #ffed4e)';
    if (props.rank === 2) return 'linear-gradient(45deg, #c0c0c0, #e5e5e5)';
    if (props.rank === 3) return 'linear-gradient(45deg, #cd7f32, #daa520)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: ${props => props.rank <= 3 ? '#333' : 'white'};
`;

const LiveCompetitions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CompetitionCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const CompetitionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CompetitionTitle = styled.div`
  font-weight: 600;
  color: #ffd700;
`;

const ParticipantCount = styled.div`
  font-size: 0.8rem;
  opacity: 0.8;
`;

const PrizePool = styled.div`
  font-size: 0.9rem;
  color: #4caf50;
  font-weight: 500;
`;

const JoinButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(76, 175, 80, 0.4);
  }
`;

// Mobile Dashboard Styled Components
const MobileDashboardContainer = styled.div`
  background: #ffffff;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  padding-bottom: 100px; /* Increased to accommodate bottom nav */
  
  /* Ensure proper mobile scaling */
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  
  /* Mobile-first responsive design */
  @media (max-width: 480px) {
    padding: 0 0 100px 0;
    font-size: 14px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    padding: 0 0 100px 0;
    font-size: 16px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  @media (min-width: 769px) {
    padding: 0 20px 100px 20px;
    font-size: 18px;
  }
  
  /* Ensure all child elements respect container boundaries */
  * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const StatusBar = styled.div`
  display: none; /* Hide the phone-like status bar */
`;

const StatusLeft = styled.div`
  display: none; /* Hide the phone-like status bar */
`;

const StatusRight = styled.div`
  display: none; /* Hide the phone-like status bar */
`;

const HeaderSection = styled.div`
  background: #1877f2;
  padding: 20px 16px; /* Use consistent padding instead of reduced padding */
  color: white;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  @media (max-width: 480px) {
    padding: 16px 12px; /* Use consistent padding */
    width: 100%;
    max-width: 100%;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    padding: 20px 16px;
    width: 100%;
    max-width: 100%;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    margin-bottom: 12px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogoText = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: white;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    justify-content: space-between;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    flex-wrap: wrap;
    align-items: center;
  }
`;

const NotificationBell = styled.div`
  position: relative;
  font-size: 16px;
  flexShrink: 0;
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ff4757;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
`;

const ProfileAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: #00d2d3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: white;
  position: relative;
  flexShrink: 0;
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
`;

const OnlineIndicator = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #2ed573;
  border-radius: 50%;
  border: 2px solid white;
`;

const GreetingSection = styled.div`
  text-align: left;
`;

const Greeting = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const UserName = styled.div`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const UserStats = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  justify-content: space-between; /* Distribute items evenly across the width */
  
  @media (max-width: 480px) {
    gap: 8px;
    justify-content: space-between; /* Keep even distribution on small screens */
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    gap: 12px;
    justify-content: space-between;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  opacity: 0.9;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 11px;
    gap: 4px;
  }
`;

const FlameIcon = styled.span`
  font-size: 14px;
`;

const TrophyIcon = styled.span`
  font-size: 14px;
`;

const StarIcon = styled.span`
  font-size: 14px;
`;

const QuickActionsSection = styled.div`
  padding: 20px 16px; /* Use consistent padding */
  background: white;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  @media (max-width: 480px) {
    padding: 16px 12px;
    width: 100%;
    max-width: 100%;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    padding: 20px 16px;
    width: 100%;
    max-width: 100%;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
  max-width: 100%;
  padding: 0 20px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: '8px';
    padding: 0 16px;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    flex-wrap: wrap;
    gap: '8px';
    padding: 0 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #2f3542;
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const ViewAllButton = styled.button`
  background: none;
  border: none;
  color: #3742fa;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 12px;
    align-self: flex-end;
  }
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  padding: 0 20px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 0 16px;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 0 20px;
  }
`;

const ActionCard = styled.div<{ variant: 'orange' | 'teal' | 'orange-yellow' | 'purple' }>`
  background: ${(props: { variant: 'orange' | 'teal' | 'orange-yellow' | 'purple' }) => {
    switch (props.variant) {
      case 'orange': return '#1877f2';
      case 'teal': return '#1877f2';
      case 'orange-yellow': return '#1877f2';
      case 'purple': return '#1877f2';
      default: return '#1877f2';
    }
  }};
  border-radius: 16px;
  padding: 20px;
  color: white;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const ActionIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ActionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const ActionSubtitle = styled.p`
  font-size: 12px;
  opacity: 0.9;
`;

const DepositSection = styled.div`
  margin: 20px 8px; /* Reduced left/right margins to use more screen width */
  background: #f1f2f6;
  border-radius: 16px;
  padding: 20px;
  width: calc(100% - 16px); /* Adjusted for new margins */
  max-width: calc(100vw - 16px);
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    margin: 16px 6px; /* Even less margin on very small screens */
    padding: 16px;
    width: calc(100vw - 12px);
    max-width: calc(100vw - 12px);
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    margin: 20px 8px;
    padding: 20px;
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
  }
`;

const DepositHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #2f3542;
`;

const WalletIcon = styled.span`
  font-size: 20px;
`;

const AmountButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
`;

const AmountButton = styled.button`
  background: #54a0ff;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  &:active {
    background: #2e86de;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    font-size: 12px;
  }
`;

const DepositInputRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const DepositInput = styled.input`
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  &::placeholder {
    color: #999;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const DepositButton = styled.button`
  background: #00d2d3;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;
  
  &:active {
    background: #00a8a9;
  }
  
  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 14px;
    width: 100%;
  }
`;

const DepositInfo = styled.p`
  font-size: 12px;
  color: #666;
  text-align: center;
`;

const BottomNavigation = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  display: flex;
  justify-content: space-around;
  padding: 12px 16px; /* Use consistent padding */
  border-top: 1px solid #e4e6ea;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  z-index: 1000;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    width: 100%;
    max-width: 100%;
    left: 0;
    right: 0;
  }
  
  @media (min-width: 481px) and (max-width: 768px) {
    padding: 12px 16px;
    width: 100%;
    max-width: 100%;
    left: 0;
    right: 0;
  }
`;

const NavItem = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: ${props => props.active ? '#1877f2' : '#65676b'};
  font-size: 12px;
  cursor: pointer;
  
  span {
    font-weight: ${props => props.active ? '600' : '400'};
  }
`;

const HomeIcon = styled.span`
  font-size: 20px;
`;

const CompeteIcon = styled.span`
  font-size: 20px;
`;

const RankingsIcon = styled.span`
  font-size: 20px;
`;

const ProfileIcon = styled.span`
  font-size: 20px;
`;

const CompetitorsDashboard: React.FC = () => {
  const { user: authUser, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // Add CSS for mobile viewport and spinning animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      /* Mobile viewport fixes */
      @media screen and (max-width: 768px) {
        body {
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
          overflow-x: hidden;
          width: 100%;
          max-width: 100%;
          background: #ffffff;
        }
        
        #root {
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }
        
        /* Prevent horizontal scrolling */
        * {
          max-width: 100%;
          box-sizing: border-box;
        }
        
        /* Ensure proper touch targets */
        button, [role="button"] {
          min-height: 44px;
          min-width: 44px;
        }
        
        /* Optimize for mobile */
        input, textarea, select {
          font-size: 16px !important;
          -webkit-appearance: none;
          border-radius: 0;
        }
        
        /* Mobile-specific container fixes */
        .mobile-container {
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        
        .mobile-section {
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
          padding-left: 8px !important;
          padding-right: 8px !important;
        }
        
        .mobile-nav {
          width: 100% !important;
          max-width: 100% !important;
          left: 0 !important;
          right: 0 !important;
        }
        
        /* Ensure all text is readable */
        * {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        /* Mobile-specific fixes for competitor cards and profile pictures */
        .competitor-card {
          width: 100% !important;
          max-width: 100% !important;
          padding: 12px !important;
          margin-bottom: 8px !important;
          border-radius: 12px !important;
          background: white !important;
          border: 1px solid #e0e0e0 !important;
          box-sizing: border-box !important;
        }
        
        .competitor-card .avatar-container {
          width: 50px !important;
          height: 50px !important;
          min-width: 50px !important;
          min-height: 50px !important;
          flex-shrink: 0 !important;
          position: relative !important;
        }
        
        .competitor-card .avatar-image {
          width: 100% !important;
          height: 100% !important;
          min-width: 50px !important;
          min-height: 50px !important;
          object-fit: cover !important;
          border-radius: 50% !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          z-index: 1 !important;
        }
        
        .competitor-card .avatar-fallback {
          width: 100% !important;
          height: 100% !important;
          min-width: 50px !important;
          min-height: 50px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 600 !important;
          font-size: 16px !important;
          color: white !important;
          background: linear-gradient(135deg, #8b5cf6, #3b82f6) !important;
          border-radius: 50% !important;
          position: relative !important;
        }
        
        .competitor-card .online-indicator {
          position: absolute !important;
          bottom: 2px !important;
          right: 2px !important;
          width: 14px !important;
          height: 14px !important;
          border-radius: 50% !important;
          border: 2px solid white !important;
          z-index: 2 !important;
        }
        
        .competitor-card .competitor-info {
          flex: 1 !important;
          min-width: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          gap: 4px !important;
        }
        
        .competitor-card .competitor-name {
          font-weight: 600 !important;
          font-size: 16px !important;
          color: #1e293b !important;
          line-height: 1.2 !important;
          max-width: 100% !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        
        .competitor-card .competitor-role {
          font-size: 12px !important;
          color: #64748b !important;
          line-height: 1.2 !important;
        }
        
        .competitor-card .competitor-stats {
          display: flex !important;
          gap: 16px !important;
          font-size: 12px !important;
          color: #64748b !important;
          flex-wrap: wrap !important;
          align-items: center !important;
          justify-content: space-between !important;
          width: 100% !important;
        }
        
        .competitor-card .stat-item {
          display: flex !important;
          align-items: center !important;
          gap: 0px !important;
          color: #fbbf24 !important;
          flex-shrink: 0 !important;
        }
        
        .competitor-card .stat-item span {
          font-weight: 500 !important;
          color: #fbbf24 !important;
        }
        
        .competitor-card .last-seen {
          font-size: 11px !important;
          color: #94a3b8 !important;
          white-space: nowrap !important;
          flex-shrink: 0 !important;
        }
        
        .competitor-card .expand-arrow {
          font-size: 12px !important;
          color: #94a3b8 !important;
          flex-shrink: 0 !important;
        }
        
        .competitor-card .action-buttons {
          background: #f8fafc !important;
          margin: 0 -16px !important;
          padding: 12px 16px !important;
          border-top: 1px solid #e2e8f0 !important;
          margin-top: 8px !important;
        }
        
        .competitor-card .button-container {
          display: flex !important;
          gap: 8px !important;
          align-items: center !important;
          flex-wrap: wrap !important;
        }
        
        .competitor-card .action-button {
          background: #3b82f6 !important;
          color: white !important;
          border: none !important;
          padding: 10px 16px !important;
          border-radius: 8px !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 6px !important;
          flex: 1 !important;
          min-width: 120px !important;
          min-height: 40px !important;
        }
        
        .competitor-card .action-button.secondary {
          background: white !important;
          color: #64748b !important;
          border: 1px solid #e2e8f0 !important;
        }
        
        /* Additional mobile optimizations for very small screens */
        @media screen and (max-width: 480px) {
          .competitor-card {
            padding: 10px !important;
            margin-bottom: 6px !important;
          }
          
          .competitor-card .avatar-container {
            width: 45px !important;
            height: 45px !important;
            min-width: 45px !important;
            min-height: 45px !important;
          }
          
          .competitor-card .avatar-image,
          .competitor-card .avatar-fallback {
            min-width: 45px !important;
            min-height: 45px !important;
          }
          
          .competitor-card .competitor-name {
            font-size: 14px !important;
          }
          
          .competitor-card .competitor-role {
            font-size: 11px !important;
          }
          
          .competitor-card .competitor-stats {
            gap: 12px !important;
            font-size: 11px !important;
          }
          
          .competitor-card .stat-item {
            gap: 0px !important;
          }
          
          .competitor-card .stat-item svg {
            width: 12px !important;
            height: 12px !important;
          }
          
          .competitor-card .action-button {
            min-width: 100px !important;
            min-height: 36px !important;
            padding: 8px 12px !important;
            font-size: 12px !important;
          }
          
          .competitor-card .action-buttons {
            margin: 0 -10px !important;
            padding: 10px 12px !important;
          }
          
          .competitor-card .button-container {
            gap: 6px !important;
          }
        }
        
        /* Fix for iOS Safari */
        @supports (-webkit-touch-callout: none) {
          body {
            -webkit-overflow-scrolling: touch;
          }
        }
        
        /* Specific fixes for section headers to prevent text cutoff */
        .mobile-section [style*="display: flex"][style*="alignItems: center"] {
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
          padding: 0 8px !important;
          margin: 0 !important;
        }
        
        .mobile-section [style*="fontSize: '16px'"] {
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
        }
        
        /* Ensure section containers use full available width */
        .mobile-section > div {
          width: calc(100% - 16px) !important;
          max-width: calc(100vw - 16px) !important;
          margin: 0 8px !important;
          box-sizing: border-box !important;
        }
      }
      }
      
      /* Prevent zoom on input focus */
      @media screen and (max-width: 768px) {
        input[type="text"],
        input[type="email"],
        input[type="password"],
        input[type="number"],
        input[type="tel"],
        textarea,
        select {
          font-size: 16px !important;
        }
      }
      
      /* Additional mobile optimizations */
      @media screen and (max-width: 480px) {
        .mobile-container {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        
        .mobile-section {
          padding-left: 4px !important;
          padding-right: 4px !important;
        }
        
        /* Ensure buttons don't overflow */
        button {
          max-width: 100% !important;
          word-wrap: break-word !important;
        }
        
        /* Header actions specific fixes */
        .mobile-section [style*="display: flex"] {
          gap: 2px !important;
          flex-wrap: wrap !important;
          justify-content: space-between !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
        }
        
        /* Ensure logout button is fully visible */
        .mobile-section button[title="Logout"] {
          padding: 4px 6px !important;
          font-size: 9px !important;
          min-width: auto !important;
          max-width: none !important;
          flex-shrink: 0 !important;
        }
        
        /* Compact header elements */
        .mobile-section [style*="fontSize: '16px'"] {
          font-size: 14px !important;
          padding: 3px !important;
        }
        
        /* Fix grid layouts for very small screens */
        .grid-responsive {
          grid-template-columns: 1fr !important;
        }
        
        /* Optimize grid positioning to use full screen width */
        .mobile-section > div > div {
          margin-left: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        
        /* Ensure action cards use full width */
        .mobile-section [style*="display: grid"] {
          margin-left: 0 !important;
          width: 100% !important;
        }
      }
      
      /* Medium screen optimizations */
      @media screen and (min-width: 481px) and (max-width: 768px) {
        .mobile-section {
          padding-left: 6px !important;
          padding-right: 6px !important;
        }
        
        .mobile-section > div > div {
          margin-left: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        
        .mobile-section [style*="display: grid"] {
          margin-left: 0 !important;
          width: 100% !important;
        }
        
        /* Header actions specific fixes for medium screens */
        .mobile-section [style*="display: flex"] {
          gap: 4px !important;
          flex-wrap: wrap !important;
          justify-content: space-between !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
        }
        
        /* Ensure logout button is fully visible on medium screens */
        .mobile-section button[title="Logout"] {
          padding: 8px 12px !important;
          font-size: 14px !important;
          min-width: auto !important;
          max-width: none !important;
          flex-shrink: 0 !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
        }
      }
      
      /* Large screen optimizations */
      @media screen and (min-width: 769px) {
        .mobile-section > div > div {
          margin-left: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        
        .mobile-section [style*="display: grid"] {
          margin-left: 0 !important;
          width: 100% !important;
        }
      }
      
      /* Ensure all content uses full screen width */
      .mobile-container * {
        max-width: 100vw !important;
        box-sizing: border-box !important;
      }
      
      /* Force grids to use full available width */
      .mobile-section [style*="display: grid"],
      .mobile-section [style*="display: grid"] > div {
        width: 100% !important;
        max-width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
      
      /* Ensure action cards and other components use full width */
      .mobile-section > div {
        width: 100% !important;
        max-width: 100% !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
      
      /* Specific fixes for header actions to prevent overflow */
      .mobile-section [style*="display: flex"][style*="alignItems: center"] {
        gap: 2px !important;
        flex-wrap: wrap !important;
        justify-content: space-between !important;
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      
              /* Ensure logout button is always visible and compact */
        .mobile-section button[title="Logout"] {
          padding: 8px 12px !important;
          font-size: 14px !important;
          min-width: auto !important;
          max-width: none !important;
          flex-shrink: 0 !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
        }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  const [competitors, setCompetitors] = useState<CompetitorExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCompetitors, setExpandedCompetitors] = useState<Set<string>>(new Set());
  const [showCompetitionInterface, setShowCompetitionInterface] = useState(false);
  const [showGroupCompetitionModal, setShowGroupCompetitionModal] = useState(false);
  const [showTicketManagement, setShowTicketManagement] = useState(false);
  const [showTicketPurchase, setShowTicketPurchase] = useState(false);
  
  // New state for filtering and pagination
  const [filters, setFilters] = useState<CompetitorFilters>({
    status: 'all',
    accountType: 'all',
    minRating: 0,
    maxRating: 5
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 6, // Changed to 6 per page
    total: 0,
    pages: 0
  });
  const [onlineCount, setOnlineCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [hasMoreCompetitors, setHasMoreCompetitors] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSilentRefreshing, setIsSilentRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [showUpdateHighlight, setShowUpdateHighlight] = useState(false);
  
  // Chat-related state
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState<{
    id: string;
    name: string;
    isOnline: boolean;
    avatar: string;
  } | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  
  // Conversations modal state
  const [showConversationsModal, setShowConversationsModal] = useState(false);
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0);
  
  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // View Profile modal state
  const [showViewProfileModal, setShowViewProfileModal] = useState(false);
  const [selectedProfileCompetitor, setSelectedProfileCompetitor] = useState<CompetitorExtended | null>(null);

  // Challenge Request modal state
  const [showChallengeRequestModal, setShowChallengeRequestModal] = useState(false);
  const [selectedChallengeCompetitor, setSelectedChallengeCompetitor] = useState<CompetitorExtended | null>(null);
  const [challengeModalMode, setChallengeModalMode] = useState<'create' | 'review' | 'edit'>('create');

  // CAPE Integration state
  const [showCAPEChallenge, setShowCAPEChallenge] = useState(false);
  const [capeChallengeData, setCapeChallengeData] = useState<any>(null);

  // CAPE Profile Sync Hook
  const { syncCAPEWithProfile } = useCAPEProfileSync(authUser?.id, false);

  // Add Finance state variables
  const [showFinanceModal, setShowFinanceModal] = useState(false);
  const [financeMode, setFinanceMode] = useState<'deposit' | 'withdrawal'>('deposit');
  const [financeAmount, setFinanceAmount] = useState<number>(10);
  const [mpesaPhoneNumber, setMpesaPhoneNumber] = useState<string>('');
  const [mpesaPin, setMpesaPin] = useState<string>('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [paymentMessage, setPaymentMessage] = useState<string>('');
  const [financeTransactions, setFinanceTransactions] = useState<FinanceTransaction[]>([]);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [currentBalance, setCurrentBalance] = useState<number>(2450);
  const [isBalanceLoaded, setIsBalanceLoaded] = useState<boolean>(false);
  
  // Safe balance setter that ensures balance is always a number
  const setSafeBalance = (value: number | null | undefined) => {
    if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
      setCurrentBalance(value);
      setIsBalanceLoaded(true);
    } else {
      console.warn('Invalid balance value:', value, 'setting to 0');
      setCurrentBalance(0);
      setIsBalanceLoaded(true);
    }
  };

  // Safe balance getter that ensures we always return a number
  const getSafeBalance = (): number => {
    if (typeof currentBalance === 'number' && !isNaN(currentBalance) && isFinite(currentBalance)) {
      return currentBalance;
    }
    console.warn('Invalid currentBalance state:', currentBalance, 'returning 0');
    return 0;
  };
  const [pendingTransactions, setPendingTransactions] = useState<FinanceTransaction[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<any>(null);

  // Get user name from AuthContext
  const getUserName = () => {
    if (authUser?.firstName && authUser?.lastName) {
      return `${authUser.firstName} ${authUser.lastName}`;
    } else if (authUser?.firstName) {
      return authUser.firstName;
    } else if (authUser?.email) {
      return authUser.email.split('@')[0];
    }
    return 'User';
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Habari za asubuhi!';
    if (hour < 17) return 'Habari za mchana!';
    return 'Habari za jioni!';
  };

  // Fallback data in case API fails
  const fallbackCompetitors: CompetitorExtended[] = [
    {
      id: '1',
      name: 'Christine Imbo',
      balance: 2800,
      wins: 12,
      rating: 4.5,
      status: 'OFFLINE',
      isOnline: false,
      avatar: 'CI',
      profilePicture: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      accountType: 'public',
      university: 'University of Nairobi',
      totalMatches: 20,
      currentStake: 300,
      lastSeen: '2 days ago'
    },
    {
      id: '2',
      name: 'John Doe',
      balance: 1500,
      wins: 8,
      rating: 4.2,
      status: 'AVAILABLE',
      isOnline: true,
      avatar: 'JD',
      accountType: 'student',
      gradeLevel: 4,
      totalMatches: 15,
      currentStake: 100
    },
    {
      id: '3',
      name: 'Sarah Wilson',
      balance: 3200,
      wins: 15,
      rating: 4.7,
      status: 'AVAILABLE',
      isOnline: true,
      avatar: 'SW',
      accountType: 'public',
      university: 'University of Nairobi',
      totalMatches: 22,
      currentStake: 250
    },
    {
      id: '4',
      name: 'Mike Johnson',
      balance: 800,
      wins: 6,
      rating: 3.8,
      status: 'OFFLINE',
      isOnline: false,
      avatar: 'MJ',
      accountType: 'student',
      gradeLevel: 5,
      totalMatches: 12,
      lastSeen: '2 hours ago'
    }
  ];

  const [userData] = useState<User>({
    id: '1',
    name: getUserName(),
    accountType: 'student',
    gradeLevel: 4,
    rating: 4.5,
    wins: 12,
    totalMatches: 18,
    isOnline: true
  });

  const [balance] = useState(2450);
  const [leaderboard] = useState<LeaderboardEntry[]>([
    { rank: 1, user: { ...userData, name: getUserName() }, score: 2850, category: 'Science' },
    { rank: 2, user: { ...userData, name: 'Sarah Wilson' }, score: 2720, category: 'Humanities' },
    { rank: 3, user: { ...userData, name: 'John Doe' }, score: 2580, category: 'Sports' }
  ]);

  const [liveCompetitions] = useState([
    { id: '1', title: 'Science Quiz Championship', participants: 24, prizePool: 5000, category: 'Science' },
    { id: '2', title: 'Math Olympiad', participants: 18, prizePool: 3000, category: 'Mathematics' },
    { id: '3', title: 'General Knowledge Battle', participants: 32, prizePool: 7500, category: 'General' }
  ]);

  // Initialize Socket.IO connection for real-time updates
  useEffect(() => {
    if (token && authUser?.id) {
      // Initialize status refresh instead of Socket.IO
      const stopRefresh = competitorService.startStatusRefresh(token, authUser.id, (silent = false) => {
        // Refresh competitors when status changes
        if (silent) {
          // Silent refresh - update data without showing loading state
          fetchCompetitorsSilent();
          fetchCompetitorsOverview();
        } else {
          // Normal refresh - show loading state
          fetchCompetitors();
          fetchCompetitorsOverview();
        }
      });
      
      return () => {
        stopRefresh();
      };
    }
  }, [token, authUser?.id]);

  const handleRefresh = useCallback(() => {
    if (token) {
      fetchCompetitors();
    }
  }, [token]);

  const fetchCompetitors = useCallback(async (isLoadMore: boolean = false) => {
    if (!token) {
      setError('No authentication token available');
      setLoading(false);
      return;
    }

    try {
      if (!isLoadMore) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);
      
      const competitorsResponse = await competitorService.getCompetitors(token, {
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
        excludeCurrentUser: true
      });
      
      if (competitorsResponse.success) {
        // Transform the API data to match our component's expected format
        const transformedCompetitors: CompetitorExtended[] = competitorsResponse.data.map(competitor => ({
          ...competitor,
          currentStake: Math.floor(Math.random() * 500) + 50 // Random stake for demo
        }));

        if (isLoadMore) {
          // Append new competitors to existing list
          setCompetitors(prev => [...prev, ...transformedCompetitors]);
        } else {
          // Replace competitors list
          setCompetitors(transformedCompetitors);
        }

        // Update pagination info
        if (competitorsResponse.pagination) {
          setPagination(prev => ({
            ...prev,
            total: competitorsResponse.pagination!.total,
            pages: competitorsResponse.pagination!.pages
          }));
          setHasMoreCompetitors(competitorsResponse.pagination!.page < competitorsResponse.pagination!.pages);
          // Update total count for overview
          setTotalCount(competitorsResponse.pagination!.total);
        }

        setLastUpdated(new Date());
        setLastUpdateTime(new Date());
        setShowUpdateHighlight(true);
        // Hide highlight after 3 seconds
        setTimeout(() => setShowUpdateHighlight(false), 3000);
      } else {
        throw new Error('Failed to fetch competitors');
      }
    } catch (err) {
      console.error('Error fetching competitors:', err);
      setError('Failed to fetch competitors. Using fallback data.');
      // Use fallback data when API fails
      if (!isLoadMore) {
        setCompetitors(fallbackCompetitors);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [token, filters, pagination.page, pagination.limit]);

  const fetchCompetitorsSilent = useCallback(async () => {
    if (!token) return;
    
    try {
      setIsSilentRefreshing(true);
      const competitorsResponse = await competitorService.getCompetitors(token, {
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
        excludeCurrentUser: true
      });

      if (competitorsResponse.success) {
        const transformedCompetitors: CompetitorExtended[] = competitorsResponse.data.map(competitor => ({
          ...competitor,
          currentStake: Math.floor(Math.random() * 500) + 50
        }));

        setCompetitors(transformedCompetitors);

        if (competitorsResponse.pagination) {
          setPagination(prev => ({
            ...prev,
            total: competitorsResponse.pagination!.total,
            pages: competitorsResponse.pagination!.pages
          }));
          setHasMoreCompetitors(competitorsResponse.pagination!.page < competitorsResponse.pagination!.pages);
        }
        setLastUpdated(new Date());
        setLastUpdateTime(new Date());
        setShowUpdateHighlight(true);
        // Hide highlight after 3 seconds
        setTimeout(() => setShowUpdateHighlight(false), 3000);
      }
    } catch (err) {
      console.error('Error fetching competitors silently:', err);
    } finally {
      // Clear silent refresh indicator after a short delay
      setTimeout(() => setIsSilentRefreshing(false), 2000);
    }
  }, [token, filters, pagination.page, pagination.limit]);

  const fetchOnlineCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const count = await competitorService.getOnlineCompetitorsCount(token);
      setOnlineCount(count);
    } catch (err) {
      console.error('Error fetching online count:', err);
    }
  }, [token]);

  const fetchCompetitorsOverview = useCallback(async () => {
    if (!token) return;
    
    try {
      const overview = await competitorService.getCompetitorsOverview(token);
      setOnlineCount(overview.onlineCount);
      setOfflineCount(overview.offlineCount);
      setTotalCount(overview.totalCount);
    } catch (err) {
      console.error('Error fetching competitors overview:', err);
    }
  }, [token]);

  // Add profile reminder functions
  const checkProfileCompleteness = useCallback(async () => {
    if (!token) return;
    
    setIsCheckingProfile(true);
    try {
      const completeness = await profileReminderService.checkProfileCompleteness();
      setProfileCompleteness(completeness);
      
      // Show reminder if profile needs attention
      const status = profileReminderService.getCompletenessStatus(completeness);
      if (status.shouldShowReminder) {
        setShowProfileReminder(true);
      }
    } catch (error: any) {
      console.error('Error checking profile completeness:', error);
      
      // Only log the error, don't show it to user unless it's not a token issue
      if (error.message && !error.message.includes('No authentication token found')) {
        console.warn('Profile completeness check failed, but continuing with dashboard');
      } else if (error.message && error.message.includes('No authentication token found')) {
        // Retry after a short delay if it's a token issue
        console.log('Token not found, retrying profile completeness check in 1 second...');
        setTimeout(() => {
          if (token) {
            checkProfileCompleteness();
          }
        }, 1000);
      }
      
      // Don't show profile reminder if service fails - don't break the dashboard
      setShowProfileReminder(false);
    } finally {
      setIsCheckingProfile(false);
    }
  }, [token]);

  const handleProfileReminderAction = (action: 'update' | 'dismiss') => {
    if (action === 'update') {
      // Navigate to profile page
      navigate('/profile');
    } else {
      setShowProfileReminder(false);
    }
  };

  const sendProfileReminder = async (reason: 'incomplete' | 'outdated' | 'visibility') => {
    try {
      await profileReminderService.sendImmediateReminder(reason);
      toast.success('Profile reminder sent! Check your notifications.');
    } catch (error) {
      console.error('Error sending profile reminder:', error);
      toast.error('Failed to send profile reminder');
    }
  };

  useEffect(() => {
    fetchCompetitors();
    fetchCompetitorsOverview();
    // Check profile completeness on mount - only if token exists
    if (token) {
      // Add a small delay to ensure token is properly stored in localStorage
      const timer = setTimeout(() => {
        checkProfileCompleteness();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [fetchCompetitors, fetchCompetitorsOverview, token]);

  // Handle pagination
  const handlePageChange = useCallback((newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasMoreCompetitors && !isLoadingMore) {
      const nextPage = pagination.page + 1;
      setPagination(prev => ({ ...prev, page: nextPage }));
      fetchCompetitors(true); // Load more mode
    }
  }, [hasMoreCompetitors, isLoadingMore, pagination.page, fetchCompetitors]);

  // Reset pagination when filters change
  const updateFilters = useCallback((newFilters: Partial<CompetitorFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    setCompetitors([]); // Clear existing competitors
  }, []);

  const handleQuickAction = (action: string) => {
    console.log(`Quick action: ${action}`);
    // Handle different quick actions
  };

  const handleChallenge = (competitorId: string) => {
    console.log(`Challenge competitor: ${competitorId}`);
    // Handle challenge logic
  };

  const handleJoinCompetition = (competitionId: string) => {
    console.log(`Join competition: ${competitionId}`);
    // Handle join competition logic
  };

  const handleCompeteClick = () => {
    console.log('Compete button clicked!');
    console.log('Current showCompetitionInterface state:', showCompetitionInterface);
    setShowCompetitionInterface(true);
    console.log('Setting showCompetitionInterface to true');
  };

  const handleCompetitionInterfaceClose = () => {
    setShowCompetitionInterface(false);
  };

  const handleGroupCompetitionClick = () => {
    setShowGroupCompetitionModal(true);
  };

  const handleGroupCompetitionModalClose = () => {
    setShowGroupCompetitionModal(false);
  };

  const handleTicketManagementClick = () => {
    setShowTicketManagement(true);
  };

  const handleTicketManagementClose = () => {
    setShowTicketManagement(false);
  };

  const handleTicketPurchaseClick = () => {
    setShowTicketPurchase(true);
  };

  const handleTicketPurchaseClose = () => {
    setShowTicketPurchase(false);
  };

  // Chat-related functions
  const handleMessageClick = async (competitor: {
    id: string;
    name: string;
    isOnline: boolean;
    avatar: string;
  }) => {
    setSelectedCompetitor(competitor);
    
    try {
      // Check if conversation exists, if not create one
      const response = await api.get(`/messages/conversations?userId=${authUser?.id}`);
      const conversations = response.data.data || [];
      
      // Find existing conversation with this competitor
      const existingConversation = conversations.find((conv: any) => 
        conv.participants.includes(competitor.id) && conv.type === 'competitor'
      );
      
      if (existingConversation) {
        setCurrentConversationId(existingConversation._id);
      } else {
        // Create new competitor conversation
        const createResponse = await api.post('/messages/conversations', {
          participants: [authUser?.id, competitor.id],
          type: 'competitor'
        });
        setCurrentConversationId(createResponse.data.data._id);
      }
      
      setShowChatModal(true);
    } catch (error) {
      console.error('Error setting up chat:', error);
      // Still show modal even if conversation setup fails
      setShowChatModal(true);
    }
  };

  const handleChatModalClose = () => {
    setShowChatModal(false);
    setSelectedCompetitor(null);
    setCurrentConversationId(null);
  };

  const handleConversationsClick = () => {
    setShowConversationsModal(true);
  };

  const handleConversationsModalClose = () => {
    setShowConversationsModal(false);
    // Refresh unread count when modal is closed
    fetchUnreadConversationsCount();
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  const handleProfileModalClose = () => {
    setShowProfileModal(false);
  };

  const handleViewProfile = (competitor: CompetitorExtended) => {
    setSelectedProfileCompetitor(competitor);
    setShowViewProfileModal(true);
  };

  const handleSendChallengeRequest = (competitor: CompetitorExtended) => {
    setSelectedChallengeCompetitor(competitor);
    setChallengeModalMode('create');
    setShowChallengeRequestModal(true);
  };

  const handleChallengeRequestSent = (data: any) => {
    // Challenge request was sent successfully
    console.log('Challenge request sent:', data);
    toast.success('Challenge request sent successfully! 🎯');
  };

  const handleChallengeRequestAccepted = (data: any) => {
    // Challenge was accepted, navigate to competition playground
    console.log('Challenge accepted:', data);
    toast.success('Challenge accepted! 🎉 Redirecting to competition...');
    setShowCompetitionInterface(true);
  };

  const handleChallengeRequestDeclined = (reason: string) => {
    // Challenge was declined
    console.log('Challenge declined:', reason);
    toast.info(`Challenge declined: ${reason}`);
  };

  const handleChallengeRequestEdited = (data: any) => {
    // Check if we're switching to edit mode or completing an edit
    if (challengeModalMode === 'review') {
      // Switching to edit mode - don't show notifications or close modal yet
      console.log('Switching to edit mode:', data);
      setChallengeModalMode('edit');
    } else {
      // Edit was completed and sent back
      console.log('Challenge request edited and sent back:', data);
      toast.success('Challenge request has been edited and sent back to the sender! 📝');
      toast.info('The sender will receive a notification to review your changes. You will be notified when they respond.');
      
      // Close the modal and refresh notifications
      handleChallengeRequestModalClose();
    }
  };

  const handleNotificationChallengeEdited = (data: any) => {
    // Handle challenge edits from notification panel
    console.log('Challenge edited from notification:', data);
    toast.success('Challenge request has been edited and sent back to the sender! 📝');
    toast.info('The sender will receive a notification to review your changes. You will be notified when they respond.');
  };

  const handleViewProfileModalClose = () => {
    setShowViewProfileModal(false);
    setSelectedProfileCompetitor(null);
  };

  const handleChallengeRequestModalClose = () => {
    setShowChallengeRequestModal(false);
    setSelectedChallengeCompetitor(null);
    setChallengeModalMode('create'); // Reset to default mode
  };

  // CAPE Integration handlers
  const handleCAPEChallengeStart = (challengeData: any) => {
    setCapeChallengeData(challengeData);
    setShowCAPEChallenge(true);
  };

  const handleCAPEChallengeClose = () => {
    setShowCAPEChallenge(false);
    setCapeChallengeData(null);
  };

  // Function to trigger CAPE content refresh when profile is updated
  const handleProfileUpdate = async () => {
    if (authUser?.id) {
      try {
        await syncCAPEWithProfile();
        toast.success('🎯 CAPE content updated with your new interests!');
      } catch (error) {
        console.error('Error updating CAPE content:', error);
        toast.warn('⚠️ CAPE content update failed, but your profile was saved');
      }
    }
  };

  // Toggle competitor card expansion
  const handleToggleCompetitorExpansion = (competitorId: string) => {
    setExpandedCompetitors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(competitorId)) {
        newSet.delete(competitorId);
      } else {
        newSet.add(competitorId);
      }
      return newSet;
    });
  };

  // Fetch unread conversations count
  const fetchUnreadConversationsCount = useCallback(async () => {
    if (!token || !authUser?.id) return;
    
    try {
      const response = await api.get(`/messages/conversations?userId=${authUser.id}`);
      if (response.data?.success) {
        const competitorConversations = response.data.data.filter((conv: any) => 
          conv.type === 'competitor'
        );
        const totalUnread = competitorConversations.reduce((sum: number, conv: any) => 
          sum + (conv.unreadCount || 0), 0
        );
        setUnreadConversationsCount(totalUnread);
      }
    } catch (error) {
      console.error('Error fetching unread conversations count:', error);
    }
  }, [token, authUser?.id]);

  // Fetch unread count on mount and set up polling
  useEffect(() => {
    fetchUnreadConversationsCount();
    const interval = setInterval(fetchUnreadConversationsCount, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [fetchUnreadConversationsCount]);

  // Finance-related functions
  const handleFinanceAction = (mode: 'deposit' | 'withdrawal') => {
    setFinanceMode(mode);
    setFinanceAmount(10); // Reset to minimum amount
    setMpesaPhoneNumber('');
    setMpesaPin('');
    setPaymentStatus('idle');
    setPaymentMessage('');
    setShowFinanceModal(true);
  };

  const handleFinanceModalClose = () => {
    setShowFinanceModal(false);
    setFinanceMode('deposit');
    setFinanceAmount(10);
    setMpesaPhoneNumber('');
    setMpesaPin('');
    setPaymentStatus('idle');
    setPaymentMessage('');
  };

  const validateFinanceInputs = (): boolean => {
    if (financeAmount < 10) {
      setPaymentMessage('Minimum amount is KES 10');
      return false;
    }
    
    if (!mpesaPhoneNumber || mpesaPhoneNumber.length < 10) {
      setPaymentMessage('Please enter a valid phone number');
      return false;
    }
    
    if (!mpesaPin || mpesaPin.length < 4) {
      setPaymentMessage('Please enter your MPESA PIN');
      return false;
    }
    
    if (financeMode === 'withdrawal' && isBalanceLoaded && financeAmount > getSafeBalance()) {
      setPaymentMessage('Insufficient balance for withdrawal');
      return false;
    }
    
    return true;
  };

  const processMPESAPayment = async () => {
    if (!validateFinanceInputs()) {
      return;
    }

    setIsProcessingPayment(true);
    setPaymentStatus('processing');
    setPaymentMessage('Processing payment...');

    try {
      // Use the real finance service to process the transaction
      const transaction = await financeService.processTransaction(
        financeMode,
        financeAmount,
        mpesaPhoneNumber,
        `${financeMode === 'deposit' ? 'Deposit' : 'Withdrawal'} via MPESA`
      );

      // Add to transactions list
      setFinanceTransactions(prev => [transaction, ...prev]);

      // Update balance
      if (financeMode === 'deposit') {
        setCurrentBalance(prev => {
          const newBalance = getSafeBalance() + financeAmount;
          return newBalance;
        });
        setPaymentStatus('success');
        setPaymentMessage(`Successfully deposited KES ${financeAmount.toLocaleString()}`);
      } else {
        setCurrentBalance(prev => {
          const newBalance = getSafeBalance() - financeAmount;
          return newBalance;
        });
        setPaymentStatus('success');
        setPaymentMessage(`Successfully withdrawn KES ${financeAmount.toLocaleString()}`);
      }

      // Show success toast
      toast.success(`${financeMode === 'deposit' ? 'Deposit' : 'Withdrawal'} successful! 🎉`);

      // Close modal after delay
      setTimeout(() => {
        handleFinanceModalClose();
      }, 2000);

    } catch (error: any) {
      console.error('Payment processing error:', error);
      setPaymentStatus('error');
      setPaymentMessage(error.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Load finance data on component mount
  useEffect(() => {
    if (token && authUser?.id) {
      loadFinanceData();
    }
  }, [token, authUser?.id]);

  // Monitor currentBalance changes for debugging
  useEffect(() => {
    console.log('currentBalance changed:', currentBalance, 'type:', typeof currentBalance);
  }, [currentBalance]);

  const loadFinanceData = async () => {
    try {
      // Load user balance
      const balanceResponse = await financeService.getUserBalance();
      console.log('Balance response:', balanceResponse); // Debug log
      if (balanceResponse.success && balanceResponse.data && typeof balanceResponse.data.amount === 'number') {
        console.log('Setting balance to:', balanceResponse.data.amount); // Debug log
        setSafeBalance(balanceResponse.data.amount);
      } else {
        console.warn('Invalid balance response structure:', balanceResponse);
        setSafeBalance(0); // Set default balance if response is invalid
      }

      // Load payment options
      const optionsResponse = await financeService.getPaymentOptions();
      if (optionsResponse.success) {
        setPaymentOptions(optionsResponse.data);
      }

      // Load recent transactions
      const historyResponse = await financeService.getPaymentHistory(5, 0);
      if (historyResponse.success) {
        setFinanceTransactions(Array.isArray(historyResponse.data) ? historyResponse.data : []);
      }

      // Load pending transactions
      const statsResponse = await financeService.getPaymentStats();
      if (statsResponse.success && statsResponse.data.totalPending > 0) {
        // Create placeholder pending transactions
        const pendingTransactions: FinanceTransaction[] = Array.from({ length: statsResponse.data.totalPending }, (_, index) => ({
          id: `pending-${index}`,
          type: 'deposit',
          amount: 0,
          status: 'pending',
          timestamp: new Date(),
          description: 'Processing...',
          phoneNumber: '',
          userType: 'competitor'
        }));
        setPendingTransactions(pendingTransactions);
      }
    } catch (error) {
      console.error('Failed to load finance data:', error);
      // Set default values on error
      setSafeBalance(0);
    }
  };

  const handleAmountButtonClick = (amount: number) => {
    setFinanceAmount(amount);
  };

  // Logout confirmation state
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Logout handler
  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/auth/login');
    toast.success('Successfully logged out! 👋');
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setFinanceAmount(value);
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'failed': return '#f44336';
      case 'cancelled': return '#9e9e9e';
      default: return '#666';
    }
  };

  const getTransactionStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'pending': return '⏳';
      case 'failed': return '❌';
      case 'cancelled': return '🚫';
      default: return '❓';
    }
  };

  // Add search state
  const [searchFilters, setSearchFilters] = useState<CompetitorSearchFilters>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<Competitor[]>([]);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [searchInfo, setSearchInfo] = useState<{
    query: string;
    matchedFields: string[];
    totalResults: number;
  } | null>(null);

  // Add profile reminder state
  const [profileCompleteness, setProfileCompleteness] = useState<ProfileCompletenessData | null>(null);
  const [showProfileReminder, setShowProfileReminder] = useState<boolean>(false);
  const [isCheckingProfile, setIsCheckingProfile] = useState<boolean>(false);

  // Add search functions
  const handleSearch = useCallback(async (filters: CompetitorSearchFilters) => {
    if (!token) return;
    
    setIsSearching(true);
    setError(null);
    
    try {
      const searchResponse = await competitorService.searchCompetitors(token, filters);
      
      if (searchResponse.success) {
        setSearchResults(searchResponse.data);
        setSearchInfo(searchResponse.searchInfo || null);
        setShowSearchResults(true);
        setLastUpdated(new Date());
      } else {
        setError('Search failed. Please try again.');
        setSearchResults([]);
        setSearchInfo(null);
      }
    } catch (err) {
      console.error('Error searching competitors:', err);
      setError('Search failed. Please try again.');
      setSearchResults([]);
      setSearchInfo(null);
    } finally {
      setIsSearching(false);
    }
  }, [token]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Auto-search as user types (with debounce)
    if (value.trim()) {
      const filters: CompetitorSearchFilters = {
        searchQuery: value,
        ...searchFilters
      };
      
      // Clear any existing timeout
      if ((window as any).searchTimeout) {
        clearTimeout((window as any).searchTimeout);
      }
      
      // Set new timeout
      (window as any).searchTimeout = setTimeout(() => {
        handleSearch(filters);
      }, 500);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
      setSearchInfo(null);
    }
  };

  const handleAdvancedSearch = (advancedFilters: Partial<CompetitorSearchFilters>) => {
    const filters: CompetitorSearchFilters = {
      ...searchFilters,
      ...advancedFilters
    };
    setSearchFilters(filters);
    handleSearch(filters);
  };

      const clearSearch = () => {
    setSearchQuery('');
    setSearchFilters({});
    setSearchResults([]);
    setSearchInfo(null);
    setShowSearchResults(false);
    // Refresh the main competitors list
    fetchCompetitors();
  };

  return (
    <MobileDashboardContainer className="mobile-container">
      {/* Header Section */}
      <HeaderSection className="mobile-section">
        <HeaderTop>
          <LogoSection>
            <LogoText>LavernAI</LogoText>
          </LogoSection>
          <HeaderActions>
            <NotificationBell>
              <NotificationCenter 
                onChallengeAccepted={handleChallengeRequestAccepted}
                onChallengeEdited={handleNotificationChallengeEdited}
                onChallengeDeclined={handleChallengeRequestDeclined}
              />
            </NotificationBell>
            <div 
              style={{ 
                position: 'relative', 
                cursor: 'pointer',
                fontSize: '14px',
                padding: '3px',
                borderRadius: '4px',
                background: 'rgba(255, 255, 255, 0.1)',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
              onClick={handleConversationsClick}
              title="Conversations"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              💬
              {unreadConversationsCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#ef4444',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  minWidth: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {unreadConversationsCount > 99 ? '99+' : unreadConversationsCount}
                </span>
              )}
            </div>
            <div 
              style={{ 
                position: 'relative', 
                cursor: 'pointer',
                fontSize: '14px',
                padding: '3px',
                borderRadius: '4px',
                background: 'rgba(255, 255, 255, 0.1)',
                flexShrink: 0
              }}
              onClick={handleTicketManagementClick}
              title="Ticket Management"
            >
              🎫
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#00d2d3',
                color: 'white',
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '10px',
                fontWeight: '600'
              }}>
                3
              </span>
            </div>
            <ProfileAvatar onClick={handleProfileClick} style={{ cursor: 'pointer' }}>
              {getUserName().split(' ').map(n => n[0]).join('').toUpperCase()}
              <OnlineIndicator />
            </ProfileAvatar>
            <button
              onClick={handleLogout}
              style={{
                background: '#dc2626',
                border: 'none',
                color: 'white',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                minWidth: 'auto',
                flexShrink: 0,
                maxWidth: 'none',
                boxSizing: 'border-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}
              title="Logout"
            >
              🚪
            </button>
          </HeaderActions>
        </HeaderTop>
        
        <GreetingSection>
          <Greeting>{getGreeting()}</Greeting>
          <UserName>{getUserName()}</UserName>
          
          <UserStats>
            <StatItem>
              <FlameIcon>🔥</FlameIcon>
              <span>12 Day Streak</span>
            </StatItem>
            <StatItem>
              <TrophyIcon>🏆</TrophyIcon>
              <span>Rank #156</span>
            </StatItem>
            <StatItem>
              <StarIcon>⭐</StarIcon>
              <span>2450 Points</span>
            </StatItem>
            <StatItem onClick={handleTicketManagementClick} style={{ cursor: 'pointer' }}>
              <span style={{ fontSize: '14px' }}>🎫</span>
              <span>3 Active Tickets</span>
            </StatItem>
          </UserStats>
        </GreetingSection>
      </HeaderSection>

      {/* Finance & MPESA Section - Immediately after header */}
      <div style={{
        background: 'white',
        padding: '20px 0',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px',
          fontSize: '16px',
          fontWeight: '600',
          color: '#2f3542',
          padding: '0 16px',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          wordWrap: 'break-word',
          wordBreak: 'break-word'
        }}>
          <span style={{ flexShrink: 0 }}>💳</span>
          <span style={{ 
            flex: 1, 
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>Finance & MPESA</span>
        </div>
        
        {/* Current Balance Summary */}
        <div style={{ 
          background: 'linear-gradient(135deg, #a55eea, #8854d0)', 
          borderRadius: '12px', 
          padding: '16px', 
          margin: '0 20px 16px 20px',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Current Balance</div>
          {!isBalanceLoaded ? (
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
              Loading...
            </div>
          ) : (
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
              KES {getSafeBalance().toLocaleString()}
            </div>
          )}
          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '12px' }}>
            {pendingTransactions.length > 0 && `${pendingTransactions.length} pending transaction(s)`}
          </div>
          
          {/* Finance Action Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleFinanceAction('deposit')}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              💰 Deposit
            </button>
            
            <button
              onClick={() => handleFinanceAction('withdrawal')}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              💸 Withdraw
            </button>
            
            <button
              onClick={() => setShowTransactionHistory(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              📊 History
            </button>
          </div>
        </div>
      </div>

      {/* Profile Update Reminder Alert */}
      {showProfileReminder && profileCompleteness && (
        <div style={{
          background: profileReminderService.getPriorityColor(
            profileReminderService.getCompletenessStatus(profileCompleteness).priority
          ),
          borderRadius: '12px',
          padding: '16px',
          margin: '16px 8px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100% - 16px)',
          boxSizing: 'border-box',
          animation: 'pulse 2s infinite',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }} onClick={() => handleProfileReminderAction('update')}>
          <span style={{ fontSize: '24px', flexShrink: 0 }}>
            {profileReminderService.getPriorityIcon(
              profileReminderService.getCompletenessStatus(profileCompleteness).priority
            )}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
              {profileReminderService.getCompletenessStatus(profileCompleteness).message}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              Profile: {Math.round(profileCompleteness.completenessPercentage)}% complete
              {profileCompleteness.missingFields.length > 0 && (
                <span> • Missing: {profileCompleteness.missingFields.slice(0, 2).join(', ')}</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleProfileReminderAction('update');
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Update Now
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleProfileReminderAction('dismiss');
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Quick Actions Section */}
      <QuickActionsSection className="mobile-section">
        <SectionHeader>
          <SectionTitle>Quick Actions</SectionTitle>
          <ViewAllButton>View All</ViewAllButton>
        </SectionHeader>
        
        {/* Online Competitors Status */}
        <div style={{ 
          marginBottom: '20px', 
          background: '#1877f2', 
          borderRadius: '16px', 
          padding: '20px',
          color: 'white',
          textAlign: 'center',
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100vw - 16px)',
          boxSizing: 'border-box',
          margin: '0 8px 20px 8px'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            {onlineCount} Competitors Online
          </div>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '12px' }}>
            {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Real-time updates active'}
          </div>
          <button
            onClick={handleRefresh}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Competitors Overview */}
        <div style={{ 
          marginBottom: '20px', 
          background: 'white', 
          borderRadius: '16px', 
          padding: '20px',
          border: '1px solid #e0e0e0',
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100vw - 16px)',
          boxSizing: 'border-box',
          margin: '0 8px 20px 8px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '16px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#2f3542',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            wordWrap: 'break-word',
            wordBreak: 'break-word'
          }}>
            <span style={{ flexShrink: 0 }}>📊</span>
            <span style={{ 
              flex: 1, 
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>Competitors Overview</span>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
            gap: '16px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#4caf50', marginBottom: '4px' }}>
                {onlineCount}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Online Now</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#ff9800', marginBottom: '4px' }}>
                {offlineCount}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Offline</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#2196f3', marginBottom: '4px' }}>
                {totalCount}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Total Users</div>
            </div>
          </div>
        </div>
        
        {/* Competitor Search Section */}
        <div style={{ 
          marginBottom: '20px', 
          background: 'white', 
          borderRadius: '16px', 
          padding: '20px',
          border: '1px solid #e0e0e0',
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100vw - 16px)',
          boxSizing: 'border-box',
          margin: '0 8px 20px 8px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '16px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#2f3542',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            wordWrap: 'break-word',
            wordBreak: 'break-word'
          }}>
            <span style={{ flexShrink: 0 }}>🔍</span>
            <span style={{ 
              flex: 1, 
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>Search Competitors</span>
          </div>
          
          {/* Main Search Input */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search by name, age, region, institution, interests..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #ddd',
                borderRadius: '12px',
                fontSize: '14px',
                boxSizing: 'border-box',
                background: '#f8f9fa'
              }}
            />
          </div>
          
          {/* Advanced Search Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '12px',
            marginBottom: '16px'
          }}>
            {/* Age Filter */}
            <div>
              <label style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px', 
                display: 'block' 
              }}>
                Age
              </label>
              <input
                type="number"
                placeholder="Age"
                onChange={(e) => {
                  const age = parseInt(e.target.value);
                  if (age > 0) {
                    handleAdvancedSearch({ age });
                  } else {
                    const { age: _, ...rest } = searchFilters;
                    setSearchFilters(rest);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Region Filter */}
            <div>
              <label style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px', 
                display: 'block' 
              }}>
                Region
              </label>
              <input
                type="text"
                placeholder="Region"
                onChange={(e) => {
                  const region = e.target.value;
                  if (region.trim()) {
                    handleAdvancedSearch({ region: region.trim() });
                  } else {
                    const { region: _, ...rest } = searchFilters;
                    setSearchFilters(rest);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Institution Filter */}
            <div>
              <label style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px', 
                display: 'block' 
              }}>
                Institution
              </label>
              <input
                type="text"
                placeholder="Institution"
                onChange={(e) => {
                  const institution = e.target.value;
                  if (institution.trim()) {
                    handleAdvancedSearch({ institution: institution.trim() });
                  } else {
                    const { institution: _, ...rest } = searchFilters;
                    setSearchFilters(rest);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Areas of Interest Filter */}
            <div>
              <label style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px', 
                display: 'block' 
              }}>
                Interests
              </label>
              <input
                type="text"
                placeholder="e.g., Humanities, Philosophy"
                onChange={(e) => {
                  const interests = e.target.value;
                  if (interests.trim()) {
                    const interestArray = interests.split(',').map(i => i.trim()).filter(Boolean);
                    handleAdvancedSearch({ areasOfInterest: interestArray });
                  } else {
                    const { areasOfInterest: _, ...rest } = searchFilters;
                    setSearchFilters(rest);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          
          {/* Search Actions */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {isSearching ? '🔍 Searching...' : 
               searchInfo ? `Found ${searchInfo.totalResults} results` : 
               'Search across all competitor data'}
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={clearSearch}
                style={{
                  background: '#f1f2f6',
                  border: '1px solid #ddd',
                  color: '#666',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
              
              <button
                onClick={() => handleSearch({ searchQuery, ...searchFilters })}
                disabled={isSearching}
                style={{
                  background: '#1877f2',
                  border: 'none',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: isSearching ? 'not-allowed' : 'pointer',
                  opacity: isSearching ? 0.6 : 1
                }}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
          
          {/* Search Info Display */}
          {searchInfo && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: '#e3f2fd',
              borderRadius: '8px',
              border: '1px solid #2196f3',
              fontSize: '11px',
              color: '#1976d2'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Search Results:</strong> {searchInfo.totalResults} competitors found
              </div>
              <div>
                <strong>Matched Fields:</strong> {searchInfo.matchedFields.join(', ')}
              </div>
            </div>
          )}
        </div>

        {/* Available Competitors Section */}
        <div style={{ marginTop: '20px' }}>
          <SectionHeader>
            <SectionTitle>
              {showSearchResults ? 'Search Results' : 'Available Competitors'}
            </SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {lastUpdateTime && (
                <div style={{ 
                  fontSize: '11px', 
                  color: '#666', 
                  background: 'rgba(255, 255, 255, 0.8)',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  whiteSpace: 'nowrap'
                }}>
                  Last updated: {lastUpdateTime.toLocaleTimeString()}
                </div>
              )}
              <ViewAllButton onClick={showSearchResults ? clearSearch : handleRefresh}>
                {showSearchResults ? 'Show All' : 'Refresh'}
              </ViewAllButton>
            </div>
          </SectionHeader>
          
          {/* Silent Refresh Indicator */}
          {isSilentRefreshing && (
            <div style={{ 
              textAlign: 'center', 
              padding: '8px', 
              marginBottom: '16px',
              background: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              color: '#2e7d32',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{ animation: 'spin 1s linear infinite' }}>🔄</span>
              <span>Updating competitor status...</span>
            </div>
          )}
          
          {isSearching ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              fontSize: '14px'
            }}>
              🔍 Searching competitors...
            </div>
          ) : error ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px', 
              background: '#fff3cd',
              borderRadius: '8px',
              border: '1px solid #ffeaa7',
              color: '#856404',
              fontSize: '14px'
            }}>
              ⚠️ {error}
            </div>
          ) : (showSearchResults ? searchResults : competitors).length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              fontSize: '14px'
            }}>
              {showSearchResults ? '😔 No competitors found matching your search criteria' : '😔 No competitors found matching your filters'}
            </div>
          ) : (
            <>
              {/* Competitors List */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px',
                transition: 'all 0.3s ease',
                width: '100%',
                maxWidth: '100%',
                ...(showUpdateHighlight && {
                  boxShadow: '0 0 20px rgba(76, 175, 80, 0.3)',
                  borderRadius: '12px',
                  padding: '8px',
                  background: 'rgba(76, 175, 80, 0.05)'
                })
              }}>
                {(showSearchResults ? searchResults : competitors).map((competitor) => (
                  <div
                    key={competitor.id}
                    className="competitor-card"
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '16px',
                      border: `1px solid ${competitor.isOnline ? '#4caf50' : '#e0e0e0'}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {/* Top Row: Avatar and Basic Info */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      width: '100%',
                      minHeight: '60px'
                    }}>
                      {/* Avatar with Online Status */}
                      <div 
                        className="avatar-container"
                        style={{ 
                          position: 'relative', 
                          flexShrink: 0,
                          width: '50px',
                          height: '50px'
                        }}
                      >
                        <div 
                          className="avatar-fallback"
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            background: competitor.isOnline 
                              ? 'linear-gradient(135deg, #8b5cf6, #3b82f6)' 
                              : 'linear-gradient(135deg, #64748b, #475569)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '600',
                            fontSize: '16px',
                            color: 'white',
                            overflow: 'hidden',
                            position: 'relative',
                            minWidth: '50px',
                            minHeight: '50px'
                          }}
                        >
                          {(competitor.profilePicture && (competitor.profilePicture.startsWith('http') || competitor.profilePicture.startsWith('data:'))) || 
                           (competitor.avatar && (competitor.avatar.startsWith('http') || competitor.avatar.startsWith('data:'))) ? (
                            <img 
                              className="avatar-image"
                              src={competitor.profilePicture || competitor.avatar} 
                              alt={`${competitor.name}'s profile`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                minWidth: '50px',
                                minHeight: '50px'
                              }}
                              onError={(e) => {
                                // Fallback to initials if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = competitor.name.substring(0, 2).toUpperCase();
                                }
                              }}
                            />
                          ) : (
                            competitor.name.substring(0, 2).toUpperCase()
                          )}
                        </div>
                        <div 
                          className="online-indicator"
                          style={{
                            position: 'absolute',
                            bottom: '2px',
                            right: '2px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background: competitor.isOnline ? '#22c55e' : '#ef4444',
                            border: '2px solid white',
                            zIndex: 1
                          }} 
                        />
                      </div>
                      
                      {/* Competitor Info */}
                      <div 
                        className="competitor-info"
                        style={{ 
                          flex: 1, 
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          marginBottom: '4px',
                          flexWrap: 'wrap',
                          width: '100%'
                        }}>
                          <span 
                            className="competitor-name"
                            style={{ 
                              fontWeight: '600', 
                              fontSize: '16px',
                              wordBreak: 'break-word',
                              cursor: 'pointer',
                              color: '#1e293b',
                              transition: 'color 0.2s ease',
                              lineHeight: '1.2',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => handleToggleCompetitorExpansion(competitor.id)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#3b82f6';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#1e293b';
                            }}
                          >
                            {competitor.name}
                          </span>
                        </div>
                        
                        <div 
                          className="competitor-role"
                          style={{ 
                            fontSize: '12px', 
                            color: '#64748b', 
                            marginBottom: '4px',
                            lineHeight: '1.2'
                          }}
                        >
                          Public User
                        </div>
                        
                        <div 
                          className="competitor-stats"
                          style={{ 
                            display: 'flex', 
                            gap: '16px', 
                            fontSize: '12px', 
                            color: '#64748b', 
                            flexWrap: 'wrap', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            width: '100%'
                          }}
                        >
                          {/* Rating and Wins grouped together */}
                          <div style={{
                            display: 'flex',
                            gap: '16px',
                            alignItems: 'center',
                            flexShrink: 0
                          }}>
                            <span 
                              className="stat-item"
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0px', 
                                color: '#fbbf24',
                                flexShrink: 0
                              }}
                            >
                              <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20" style={{ color: '#fbbf24' }}>
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                              </svg>
                              <span style={{ fontWeight: '500', color: '#fbbf24' }}>{competitor.rating}</span>
                            </span>
                            <span 
                              className="stat-item"
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0px', 
                                color: '#fbbf24',
                                flexShrink: 0
                              }}
                            >
                              <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20" style={{ color: '#fbbf24' }}>
                                <path d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
                              </svg>
                              <span style={{ fontWeight: '500', color: '#fbbf24' }}>{competitor.wins}/{competitor.totalMatches}</span>
                            </span>
                          </div>
                          
                          {/* Right side: Last seen and expand arrow */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginLeft: 'auto'
                          }}>
                            {!competitor.isOnline && competitor.lastSeen && (
                              <span 
                                className="last-seen"
                                style={{ 
                                  fontSize: '11px', 
                                  color: '#94a3b8', 
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                {competitor.lastSeen}
                              </span>
                            )}
                            <div 
                              className="expand-arrow"
                              style={{ 
                                fontSize: '12px', 
                                color: '#94a3b8', 
                                transition: 'transform 0.2s ease',
                                transform: expandedCompetitors.has(competitor.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                flexShrink: 0
                              }}
                            >
                              ⌄
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Middle Row: Educational and Location Info */}
                    {(competitor.educationalInfo || competitor.personalInfo) && (
                      <div style={{ 
                        padding: '8px 12px', 
                        background: '#f8f9fa', 
                        borderRadius: '8px',
                        fontSize: '11px',
                        color: '#666',
                        lineHeight: '1.4'
                      }}>
                        {/* Enhanced Educational Information */}
                        {competitor.educationalInfo && (
                          <div style={{ marginBottom: '4px' }}>
                            {competitor.educationalInfo.isCurrentlyStudying && competitor.educationalInfo.institution && competitor.educationalInfo.courseOfStudy && competitor.educationalInfo.yearOfStudy ? (
                              <div>
                                {competitor.educationalInfo.yearOfStudy === 1 ? '1st' : 
                                 competitor.educationalInfo.yearOfStudy === 2 ? '2nd' : 
                                 competitor.educationalInfo.yearOfStudy === 3 ? '3rd' : 
                                 competitor.educationalInfo.yearOfStudy + 'th'} Year {competitor.educationalInfo.courseOfStudy}
                              </div>
                            ) : competitor.educationalInfo.institution ? (
                              <div>Studying at {competitor.educationalInfo.institution}</div>
                            ) : null}
                          </div>
                        )}
                        
                        {/* Location Information */}
                        {competitor.personalInfo && (
                          <div>📍 {competitor.personalInfo.city || competitor.personalInfo.province || competitor.personalInfo.region || competitor.personalInfo.country}</div>
                        )}
                      </div>
                    )}
                    
                    {/* Bottom Row: Action Buttons - Only show when expanded */}
                    {expandedCompetitors.has(competitor.id) && (
                      <div 
                        className="action-buttons"
                        style={{ 
                          background: '#f8fafc',
                          margin: '0 -16px',
                          padding: '12px 16px',
                          borderTop: '1px solid #e2e8f0',
                          marginTop: '8px'
                        }}
                      >
                        <div 
                          className="button-container"
                          style={{ 
                            display: 'flex', 
                            gap: '8px', 
                            alignItems: 'center',
                            flexWrap: 'wrap'
                          }}
                        >
                        {/* View Profile Button */}
                        <button
                          className="action-button"
                          onClick={() => handleViewProfile(competitor)}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            flex: 1,
                            minWidth: '120px',
                            minHeight: '40px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#2563eb';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#3b82f6';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                          </svg>
                          View Profile
                        </button>
                        
                        {/* Message Button - Always available */}
                        <button
                          className="action-button secondary"
                          onClick={() => handleMessageClick({
                            id: competitor.id,
                            name: competitor.name,
                            isOnline: competitor.isOnline,
                            avatar: (competitor.profilePicture && (competitor.profilePicture.startsWith('http') || competitor.profilePicture.startsWith('data:'))) || 
                                    (competitor.avatar && (competitor.avatar.startsWith('http') || competitor.avatar.startsWith('data:'))) 
                                    ? (competitor.profilePicture || competitor.avatar) 
                                    : competitor.name.substring(0, 2).toUpperCase()
                          })}
                          style={{
                            background: 'white',
                            color: '#64748b',
                            border: '1px solid #e2e8f0',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            flex: 1,
                            minWidth: '120px',
                            minHeight: '40px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f1f5f9';
                            e.currentTarget.style.color = '#475569';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.color = '#64748b';
                          }}
                        >
                          <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                          </svg>
                          Message
                        </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {pagination.pages > 1 && !showSearchResults && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '8px', 
                  marginTop: '20px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: pagination.page <= 1 ? '#f5f5f5' : 'white',
                      borderRadius: '8px',
                      cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      minWidth: '80px'
                    }}
                  >
                    ← Previous
                  </button>
                  
                  <span style={{ 
                    padding: '8px 12px', 
                    fontSize: '12px', 
                    color: '#666',
                    textAlign: 'center',
                    minWidth: '120px'
                  }}>
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  
                  <button
                    onClick={handleLoadMore}
                    disabled={!hasMoreCompetitors || isLoadingMore}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      background: !hasMoreCompetitors || isLoadingMore ? '#f5f5f5' : 'white',
                      borderRadius: '8px',
                      cursor: !hasMoreCompetitors || isLoadingMore ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      minWidth: '80px'
                    }}
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        <QuickActionsGrid>
          <ActionCard variant="teal" onClick={handleCompeteClick}>
            <ActionIcon>⚡</ActionIcon>
            <ActionTitle>Compete</ActionTitle>
            <ActionSubtitle>Challenge & compete</ActionSubtitle>
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: '#ff4757',
              color: 'white',
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '10px',
              fontWeight: '600'
            }}>
              🎫 Required
            </div>
          </ActionCard>
          
          <ActionCard variant="orange-yellow" onClick={() => handleQuickAction('practice')}>
            <ActionIcon>📖</ActionIcon>
            <ActionTitle>Practice Mode</ActionTitle>
            <ActionSubtitle>Improve your skills</ActionSubtitle>
          </ActionCard>
          
          <ActionCard variant="purple" onClick={handleGroupCompetitionClick}>
            <ActionIcon>🚢</ActionIcon>
            <ActionTitle>LAVERN TITAN</ActionTitle>
            <ActionSubtitle>Group competitions</ActionSubtitle>
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: '#ff4757',
              color: 'white',
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '10px',
              fontWeight: '600'
            }}>
              🎫 Required
            </div>
          </ActionCard>

          {/* CAPE Integration Card */}
          <CAPEIntegration
            competitorId={authUser?.id || ''}
            competitorName={getUserName()}
            university={authUser?.university}
            courseOfStudy={authUser?.courseOfStudy}
            onChallengeStart={handleCAPEChallengeStart}
          />
        </QuickActionsGrid>
        
        {/* Ticket Management Section */}
        <div style={{ marginTop: '20px', width: '100%', maxWidth: '100%' }}>
          <SectionHeader>
            <SectionTitle>Ticket Management</SectionTitle>
            <ViewAllButton onClick={handleTicketManagementClick}>Manage Tickets</ViewAllButton>
          </SectionHeader>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '12px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <ActionCard variant="teal" onClick={handleTicketPurchaseClick}>
              <ActionIcon>🎫</ActionIcon>
              <ActionTitle>Buy Tickets</ActionTitle>
              <ActionSubtitle>Purchase competition tickets</ActionSubtitle>
            </ActionCard>
            
            <ActionCard variant="purple" onClick={handleTicketManagementClick}>
              <ActionIcon>📋</ActionIcon>
              <ActionTitle>My Tickets</ActionTitle>
              <ActionSubtitle>View & manage tickets</ActionSubtitle>
            </ActionCard>
          </div>
          
          {/* Ticket Usage Guide */}
          <div style={{
            marginTop: '16px',
            padding: '16px',
            background: '#f8f9fa',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#2f3542'
            }}>
              <span>💡</span>
              <span>How to Use Tickets</span>
            </div>
            <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
              1. <strong>Buy tickets</strong> in your preferred class (Third, Second, First, Premium, VIP)<br/>
              2. <strong>Set a PIN</strong> for security when purchasing<br/>
              3. <strong>Use tickets</strong> to enter competitions by entering your PIN<br/>
              4. <strong>Track usage</strong> and manage your ticket portfolio
            </div>
          </div>
        </div>
        
        {/* Ticket Status Overview */}
        <div style={{ 
          marginTop: '20px', 
          background: '#1877f2', 
          borderRadius: '16px', 
          padding: '20px',
          color: 'white',
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100vw - 16px)',
          boxSizing: 'border-box',
          margin: '0 8px 20px 8px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Ticket Status</h3>
            <button
              onClick={handleTicketManagementClick}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              View All
            </button>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '16px',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#4caf50' }}>3</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Active</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#ff9800' }}>2</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Used</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#9c27b0' }}>1</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Expired</div>
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#00d2d3' }}>KES 2,500</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Total Value</div>
            </div>
          </div>
        </div>
      </QuickActionsSection>

      {/* Bottom Navigation */}
      <BottomNavigation className="mobile-nav">
        <NavItem active>
          <HomeIcon>🏠</HomeIcon>
          <span>Home</span>
        </NavItem>
        <NavItem>
          <CompeteIcon>🏆</CompeteIcon>
          <span>Compete</span>
        </NavItem>
        <NavItem onClick={handleTicketManagementClick}>
          <span style={{ fontSize: '20px' }}>🎫</span>
          <span>Tickets</span>
        </NavItem>
        <NavItem>
          <RankingsIcon>👥</RankingsIcon>
          <span>Rankings</span>
        </NavItem>
        <NavItem onClick={handleProfileClick}>
          <ProfileIcon>⚙️</ProfileIcon>
          <span>Profile</span>
        </NavItem>
      </BottomNavigation>

      {showCompetitionInterface && (
        <>
          {console.log('Rendering CompetitionInterface, showCompetitionInterface:', showCompetitionInterface)}
          <CompetitionInterface isOpen={showCompetitionInterface} onClose={handleCompetitionInterfaceClose} />
        </>
      )}
      {showGroupCompetitionModal && (
        <GroupCompetitionModal 
          onClose={handleGroupCompetitionModalClose} 
          currentUserId={authUser?.id}
        />
      )}
      
      {/* Ticket Management Modal */}
      {showTicketManagement && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            height: '90%',
            maxWidth: '1200px',
            maxHeight: '800px'
          }}>
            <button
              onClick={handleTicketManagementClose}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#ff4757',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '20px',
                cursor: 'pointer',
                zIndex: 1001
              }}
            >
              ✕
            </button>
            <TicketManagement />
          </div>
        </div>
      )}
      
      {/* Ticket Purchase Modal */}
      {showTicketPurchase && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            height: '90%',
            maxWidth: '1200px',
            maxHeight: '800px',
            overflow: 'auto'
          }}>
            <button
              onClick={handleTicketPurchaseClose}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#ff4757',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '20px',
                cursor: 'pointer',
                zIndex: 1001
              }}
            >
              ✕
            </button>
            <TicketPurchase 
              userBalance={balance}
              onBack={handleTicketPurchaseClose}
              onTicketPurchased={(ticket) => {
                console.log('Ticket purchased:', ticket);
                handleTicketPurchaseClose();
                // You can add additional logic here like refreshing tickets
              }}
            />
          </div>
        </div>
      )}
      
      {/* Competitor Chat Modal */}
      {showChatModal && selectedCompetitor && (
        <CompetitorChatModal
          isOpen={showChatModal}
          onClose={handleChatModalClose}
          currentUser={{
            id: authUser?.id || '',
            name: getUserName(),
            role: 'student'
          }}
          competitor={selectedCompetitor}
          conversationId={currentConversationId || undefined}
        />
      )}
      
      {/* Competitor Conversations Modal */}
      {showConversationsModal && (
        <CompetitorConversations
          isOpen={showConversationsModal}
          onClose={handleConversationsModalClose}
          currentUser={{
            id: authUser?.id || '',
            name: getUserName(),
            role: 'student'
          }}
        />
      )}

      {/* CAPE Enhanced Competition Interface Modal */}
      {showCAPEChallenge && capeChallengeData && (
        <CAPEEnhancedCompetitionInterface
          isOpen={showCAPEChallenge}
          onClose={handleCAPEChallengeClose}
          competitor={{
            id: authUser?.id || '',
            name: getUserName(),
            university: authUser?.university,
            courseOfStudy: authUser?.courseOfStudy,
            rating: 4.5,
            wins: 12,
            totalMatches: 20,
            avatar: getUserName().split(' ').map(n => n[0]).join('').toUpperCase(),
            isOnline: true,
            accountType: 'student' as const,
            balance: getSafeBalance(),
            status: 'AVAILABLE' as const
          }}
          challengeData={capeChallengeData}
        />
      )}
      
      {/* Competitor Profile Modal */}
      {showProfileModal && (
        <CompetitorProfile
          isOpen={showProfileModal}
          onClose={handleProfileModalClose}
        />
      )}

      {/* View Profile Modal */}
      {showViewProfileModal && selectedProfileCompetitor && (
        <ViewProfileModal
          isOpen={showViewProfileModal}
          onClose={handleViewProfileModalClose}
          competitor={selectedProfileCompetitor}
          onSendChallengeRequest={handleSendChallengeRequest}
        />
      )}

      {/* Challenge Request Modal */}
      {showChallengeRequestModal && selectedChallengeCompetitor && (
        <ChallengeRequestModal
          isOpen={showChallengeRequestModal}
          onClose={handleChallengeRequestModalClose}
          competitor={selectedChallengeCompetitor}
          mode={challengeModalMode}
          onRequestSent={handleChallengeRequestSent}
          onRequestAccepted={handleChallengeRequestAccepted}
          onRequestDeclined={handleChallengeRequestDeclined}
          onRequestEdited={handleChallengeRequestEdited}
        />
      )}

      {/* Finance Modal */}
      {showFinanceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={handleFinanceModalClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#ff4757',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>

            {/* Modal Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
                {financeMode === 'deposit' ? '💰 Deposit Funds' : '💸 Withdraw Funds'}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {financeMode === 'deposit' 
                  ? 'Add money to your account via MPESA' 
                  : 'Withdraw money to your MPESA account'
                }
              </div>
            </div>

            {/* Amount Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px', 
                display: 'block',
                color: '#2f3542'
              }}>
                Amount (KES)
              </label>
              <input
                type="number"
                value={financeAmount}
                onChange={handleCustomAmountChange}
                min="10"
                step="1"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter amount (min: 10)"
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px',
                textAlign: 'center'
              }}>
                Minimum: KES 10
                {financeMode === 'withdrawal' && (
                  <span style={{ marginLeft: '8px' }}>
                    • Available: KES {isBalanceLoaded ? getSafeBalance().toLocaleString() : 'Loading...'}
                  </span>
                )}
              </div>
            </div>

            {/* MPESA Phone Number */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px', 
                display: 'block',
                color: '#2f3542'
              }}>
                MPESA Phone Number
              </label>
              <input
                type="tel"
                value={mpesaPhoneNumber}
                onChange={(e) => setMpesaPhoneNumber(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="e.g., 07XXXXXXXX"
                maxLength={10}
              />
            </div>

            {/* MPESA PIN */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '8px', 
                display: 'block',
                color: '#2f3542'
              }}>
                MPESA PIN
              </label>
              <input
                type="password"
                value={mpesaPin}
                onChange={(e) => setMpesaPin(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter your MPESA PIN"
                maxLength={4}
              />
            </div>

            {/* Payment Status */}
            {paymentStatus !== 'idle' && (
              <div style={{
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center',
                background: paymentStatus === 'success' ? '#e8f5e8' : 
                           paymentStatus === 'error' ? '#ffebee' : '#fff3e0',
                border: `1px solid ${
                  paymentStatus === 'success' ? '#4caf50' : 
                  paymentStatus === 'error' ? '#f44336' : '#ff9800'
                }`,
                color: paymentStatus === 'success' ? '#2e7d32' : 
                       paymentStatus === 'error' ? '#c62828' : '#ef6c00'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                  {paymentStatus === 'processing' && '⏳ Processing...'}
                  {paymentStatus === 'success' && '✅ Success!'}
                  {paymentStatus === 'error' && '❌ Error'}
                </div>
                <div style={{ fontSize: '12px' }}>
                  {paymentMessage}
                </div>
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={processMPESAPayment}
              disabled={isProcessingPayment || paymentStatus === 'success'}
              style={{
                width: '100%',
                padding: '16px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isProcessingPayment || paymentStatus === 'success' ? 'not-allowed' : 'pointer',
                background: isProcessingPayment || paymentStatus === 'success' 
                  ? '#ccc' 
                  : financeMode === 'deposit' 
                    ? 'linear-gradient(45deg, #4caf50, #8bc34a)' 
                    : 'linear-gradient(45deg, #ff9800, #ff5722)',
                color: 'white',
                transition: 'all 0.2s ease'
              }}
            >
              {isProcessingPayment ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span style={{ animation: 'spin 1s linear infinite' }}>🔄</span>
                  Processing...
                </span>
              ) : paymentStatus === 'success' ? (
                'Completed!'
              ) : (
                `${financeMode === 'deposit' ? '💰' : '💸'} ${financeMode === 'deposit' ? 'Deposit' : 'Withdraw'} KES ${financeAmount.toLocaleString()}`
              )}
            </button>

            {/* Additional Information */}
            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.4'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>Processing Time:</strong> Instant for deposits, 2-24 hours for withdrawals
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Fees:</strong> No fees for deposits, 1% for withdrawals
              </div>
              <div>
                <strong>Security:</strong> All transactions are encrypted and secure
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History Modal */}
      {showTransactionHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowTransactionHistory(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#ff4757',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>

            {/* Modal Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
                📊 Transaction History
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Complete record of all your financial transactions
              </div>
            </div>

            {/* Transaction List */}
            {(financeTransactions || []).length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#666',
                fontSize: '14px'
              }}>
                📝 No transactions yet
                <div style={{ marginTop: '8px', fontSize: '12px' }}>
                  Start by making your first deposit or withdrawal
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(financeTransactions || []).map((transaction) => (
                  <div
                    key={transaction.id}
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '16px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    {/* Transaction Header */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>
                          {transaction.type === 'deposit' ? '💰' : '💸'}
                        </span>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: '600' }}>
                            {transaction.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {transaction.timestamp.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: '700',
                          color: transaction.type === 'deposit' ? '#4caf50' : '#f44336'
                        }}>
                          {transaction.type === 'deposit' ? '+' : '-'}KES {transaction.amount.toLocaleString()}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: getTransactionStatusColor(transaction.status),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          justifyContent: 'flex-end'
                        }}>
                          {getTransactionStatusIcon(transaction.status)} {transaction.status.toUpperCase()}
                        </div>
                      </div>
                    </div>

                    {/* Transaction Details */}
                    <div style={{ 
                      padding: '12px', 
                      background: 'white', 
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Description:</strong> {transaction.description}
                      </div>
                      {transaction.mpesaReference && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>MPESA Ref:</strong> {transaction.mpesaReference}
                        </div>
                      )}
                      {transaction.fee && transaction.fee > 0 && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Fee:</strong> KES {transaction.fee.toLocaleString()}
                        </div>
                      )}
                      {transaction.netAmount && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Net Amount:</strong> KES {transaction.netAmount.toLocaleString()}
                        </div>
                      )}
                      <div>
                        <strong>Transaction ID:</strong> {transaction.id}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <div style={{ 
              marginTop: '20px', 
              padding: '16px', 
              background: 'linear-gradient(135deg, #667eea, #764ba2)', 
              borderRadius: '12px',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>
                Transaction Summary
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                gap: '16px',
                textAlign: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>
                    {(financeTransactions || []).filter(t => t.type === 'deposit').length}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Deposits</div>
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>
                    {(financeTransactions || []).filter(t => t.type === 'withdrawal').length}
                  </div>
                  <div style={{ fontSize: '12px', opacity: '0.8' }}>Withdrawals</div>
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>
                    {(financeTransactions || []).filter(t => t.status === 'completed').length}
                  </div>
                  <div style={{ fontSize: '12px', opacity: '0.8' }}>Completed</div>
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700' }}>
                    {(financeTransactions || []).filter(t => t.status === 'pending').length}
                  </div>
                  <div style={{ fontSize: '12px', opacity: '0.8' }}>Pending</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              🚪
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1f2937'
            }}>
              Confirm Logout
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Are you sure you want to log out? You'll need to sign in again to access your account.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={cancelLogout}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  color: '#374151',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  background: 'linear-gradient(45deg, #dc2626, #ef4444)',
                  border: 'none',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileDashboardContainer>
  );
};

export default CompetitorsDashboard;
